import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  useScopeStream,
  type CacheEndpointInfo,
  type CacheEndpointStatus,
  type CacheRequestEvent,
} from '../hooks/useScopeStream.js';
import { api } from '../lib/api.js';

export interface ImportDashboardProps {
  sessionId: string;
  /**
   * Live session status from the parent query. Drives which control buttons
   * are enabled. Re-renders cause the dashboard to update.
   */
  status: 'running' | 'paused' | 'aborted' | 'completed' | 'failed' | string;
  warningCount?: number;
  /**
   * Optional recipe definition so we can compute parent → child indentation
   * for endpoints. Falls back to flat ordering if absent.
   */
  recipeYaml?: string | null;
  /** Called after Pause / Resume / Abort succeeds so the parent can refetch. */
  onControlChange?: () => void;
}

const STATUS_PILL: Record<CacheEndpointStatus, { bg: string; text: string; ring: string; label: string; dot: string }> = {
  started:           { bg: 'rgba(56,189,248,0.15)',  text: 'rgb(125,211,252)', ring: 'rgba(56,189,248,0.45)',  label: 'starting',  dot: 'rgb(125,211,252)' },
  progress:          { bg: 'rgba(250,204,21,0.15)',  text: 'rgb(250,204,21)',  ring: 'rgba(250,204,21,0.45)',  label: 'fetching',  dot: 'rgb(250,204,21)'  },
  ok:                { bg: 'rgba(13,207,184,0.18)',  text: 'rgb(94,234,212)',  ring: 'rgba(13,207,184,0.45)',  label: 'done',      dot: 'rgb(94,234,212)'  },
  failed:            { bg: 'rgba(244,63,94,0.18)',   text: 'rgb(253,164,175)', ring: 'rgba(244,63,94,0.45)',   label: 'failed',    dot: 'rgb(253,164,175)' },
  rescope_triggered: { bg: 'rgba(251,146,60,0.18)',  text: 'rgb(253,186,116)', ring: 'rgba(251,146,60,0.45)',  label: 'rescope',   dot: 'rgb(253,186,116)' },
  auth_failed:       { bg: 'rgba(251,146,60,0.18)',  text: 'rgb(253,186,116)', ring: 'rgba(251,146,60,0.45)',  label: 'auth fail', dot: 'rgb(253,186,116)' },
};

const SESSION_PILL: Record<string, { bg: string; text: string; ring: string }> = {
  running:   { bg: 'rgba(250,204,21,0.18)', text: 'rgb(250,204,21)',  ring: 'rgba(250,204,21,0.55)' },
  paused:    { bg: 'rgba(56,189,248,0.18)', text: 'rgb(125,211,252)', ring: 'rgba(56,189,248,0.55)' },
  aborted:   { bg: 'rgba(148,163,184,0.18)', text: 'rgb(203,213,225)', ring: 'rgba(148,163,184,0.55)' },
  completed: { bg: 'rgba(13,207,184,0.18)', text: 'rgb(94,234,212)',  ring: 'rgba(13,207,184,0.55)' },
  failed:    { bg: 'rgba(244,63,94,0.18)',  text: 'rgb(253,164,175)', ring: 'rgba(244,63,94,0.55)'  },
};

interface DependencyTree {
  /** Endpoints in display order (parent before children). */
  ordered: string[];
  /** Indent depth per endpoint (0 = root, 1 = depends on a root, etc.). */
  depth: Record<string, number>;
}

/**
 * Parse the recipe YAML to extract the parent → child relationships so we can
 * indent dependent endpoints under their parents. Falls back to a flat sort.
 *
 * The recipe schema's `dependsOn` field is a single string, so the dependency
 * graph is a forest. We do a stable depth-first walk so siblings stay grouped.
 */
function buildDependencyTree(recipeYaml: string | null | undefined, knownEndpoints: string[]): DependencyTree {
  if (!recipeYaml) return { ordered: [...knownEndpoints].sort(), depth: Object.fromEntries(knownEndpoints.map((k) => [k, 0])) };
  // Cheap parse — we only need the endpoints map. Avoid a full yaml-parse dep here.
  const dependsOn: Record<string, string | null> = {};
  for (const ep of knownEndpoints) {
    const re = new RegExp(`^  ${ep}:[\\s\\S]*?(?=^  [a-zA-Z]|$)`, 'm');
    const block = recipeYaml.match(re)?.[0] ?? '';
    const m = block.match(/dependsOn:\s*([^\n]+)/);
    dependsOn[ep] = m && m[1] ? m[1].trim() : null;
  }
  const childrenOf: Record<string, string[]> = {};
  const roots: string[] = [];
  for (const ep of knownEndpoints) {
    const parent = dependsOn[ep];
    if (parent && knownEndpoints.includes(parent)) {
      (childrenOf[parent] ??= []).push(ep);
    } else {
      roots.push(ep);
    }
  }
  const ordered: string[] = [];
  const depth: Record<string, number> = {};
  function walk(name: string, d: number) {
    if (depth[name] !== undefined) return; // cycle protection
    depth[name] = d;
    ordered.push(name);
    for (const child of childrenOf[name] ?? []) walk(child, d + 1);
  }
  roots.sort().forEach((r) => walk(r, 0));
  // Any orphan (parent not in known set) gets appended as root
  for (const ep of knownEndpoints) if (depth[ep] === undefined) walk(ep, 0);
  return { ordered, depth };
}

function formatBytes(n: number | undefined): string {
  if (!n || n < 1024) return `${n ?? 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms: number | undefined): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Status dot — pulses when the endpoint is actively fetching (yellow).
 * Solid colors for terminal states. Pulse uses CSS animation already in
 * the global stylesheet (or falls back to flat color if missing).
 */
function StatusDot({ status }: { status: CacheEndpointStatus }) {
  const s = STATUS_PILL[status];
  const pulse = status === 'progress' || status === 'started';
  return (
    <span
      aria-label={s.label}
      className="inline-block h-2 w-2 rounded-full shrink-0"
      style={{
        background: s.dot,
        boxShadow: pulse ? `0 0 0 3px ${s.bg}` : undefined,
        animation: pulse ? 'pulse 1.6s cubic-bezier(0.4, 0, 0.6, 1) infinite' : undefined,
      }}
    />
  );
}

function StatusPill({ status }: { status: CacheEndpointStatus }) {
  const s = STATUS_PILL[status];
  return (
    <span
      className="rounded px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest"
      style={{ background: s.bg, color: s.text, border: `1px solid ${s.ring}` }}
    >
      {s.label}
    </span>
  );
}

function EndpointCard({
  name,
  info,
  depth,
  expanded,
  onToggle,
}: {
  name: string;
  info: CacheEndpointInfo;
  depth: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const records = info.recordsFetched;
  const canonOk = info.canonicalOkCount ?? 0;
  const canonErr = info.canonicalErrCount ?? 0;
  const hasError = !!info.error;

  return (
    <div
      className="rounded-xl px-3 py-2.5 text-sm"
      style={{
        background: 'rgba(15,23,42,0.4)',
        border: `1px solid ${hasError ? 'rgba(244,63,94,0.45)' : 'rgba(148,163,184,0.18)'}`,
        marginLeft: depth * 18,
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 text-left"
      >
        <StatusDot status={info.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs truncate text-text-primary">{name}</span>
            {info.resourceType && (
              <span
                className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ background: 'rgba(56,189,248,0.12)', color: 'rgb(125,211,252)' }}
              >
                {info.resourceType}
              </span>
            )}
            <StatusPill status={info.status} />
          </div>
          <div className="text-xs text-text-dimmed font-mono mt-1 flex items-center gap-3">
            <span>{records.toLocaleString()} fetched</span>
            {info.page > 0 && <span>· pg {info.page}</span>}
            {info.totalRequests !== undefined && <span>· {info.totalRequests} req</span>}
            {info.totalBytes !== undefined && <span>· {formatBytes(info.totalBytes)}</span>}
            {(canonOk > 0 || canonErr > 0) && (
              <span>· canon {canonOk}✓ {canonErr > 0 ? `${canonErr}✗` : ''}</span>
            )}
            {info.durationMs !== undefined && <span>· {formatDuration(info.durationMs)}</span>}
          </div>
        </div>
        <span className="text-text-dimmed text-xs select-none">{expanded ? '▾' : '▸'}</span>
      </button>
      {hasError && (
        <div
          className="mt-2 rounded px-2 py-1.5 text-xs font-mono"
          style={{ background: 'rgba(244,63,94,0.1)', color: 'rgb(253,164,175)', border: '1px solid rgba(244,63,94,0.3)' }}
          title={info.error}
        >
          {info.error}
        </div>
      )}
      {expanded && info.recentRequests && info.recentRequests.length > 0 && (
        <div className="mt-3 space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-text-secondary">Recent requests</div>
          {info.recentRequests.map((r) => (
            <RequestRow key={r.id} req={r} />
          ))}
        </div>
      )}
      {expanded && (!info.recentRequests || info.recentRequests.length === 0) && (
        <div className="mt-3 text-xs text-text-dimmed italic">No HTTP requests captured yet for this endpoint.</div>
      )}
    </div>
  );
}

function RequestRow({ req }: { req: CacheRequestEvent }) {
  const ok = req.status >= 200 && req.status < 400;
  const statusColor = ok ? 'rgb(94,234,212)' : 'rgb(253,164,175)';
  const path = (() => {
    try { return new URL(req.url).pathname; } catch { return req.url; }
  })();
  return (
    <div
      className="rounded px-2 py-1 text-xs font-mono flex items-center gap-2"
      style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(148,163,184,0.12)' }}
      title={req.url}
    >
      <span className="text-text-dimmed shrink-0">{req.method}</span>
      <span className="truncate flex-1 text-text-primary">{path}</span>
      <span className="shrink-0" style={{ color: statusColor }}>{req.status}</span>
      <span className="shrink-0 text-text-dimmed">{formatDuration(req.durationMs)}</span>
      <span className="shrink-0 text-text-dimmed">{formatBytes(req.bytes)}</span>
      <span className="shrink-0 text-text-dimmed">{req.recordsFetched} rec</span>
    </div>
  );
}

export function ImportDashboard({ sessionId, status, warningCount, recipeYaml, onControlChange }: ImportDashboardProps) {
  const { cacheEndpoints, sessionCompleted } = useScopeStream(sessionId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<null | 'pause' | 'resume' | 'abort'>(null);
  const [confirmAbort, setConfirmAbort] = useState(false);

  const tree = useMemo(
    () => buildDependencyTree(recipeYaml, Object.keys(cacheEndpoints)),
    [recipeYaml, cacheEndpoints],
  );

  const totals = useMemo(() => {
    let records = 0, ok = 0, fail = 0, running = 0, requests = 0, bytes = 0;
    for (const info of Object.values(cacheEndpoints)) {
      records += info.recordsFetched;
      requests += info.totalRequests ?? 0;
      bytes += info.totalBytes ?? 0;
      if (info.status === 'ok') ok += 1;
      else if (['failed', 'auth_failed', 'rescope_triggered'].includes(info.status)) fail += 1;
      else running += 1;
    }
    return { records, ok, fail, running, requests, bytes };
  }, [cacheEndpoints]);

  const sessStyle = SESSION_PILL[status] ?? SESSION_PILL.running!;
  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const isTerminal = status === 'completed' || status === 'failed' || status === 'aborted';

  async function handleAction(action: 'pause' | 'resume' | 'abort') {
    setBusy(action);
    try {
      if (action === 'pause') await api.pauseSession(sessionId);
      else if (action === 'resume') await api.resumeSession(sessionId);
      else if (action === 'abort') await api.abortSession(sessionId);
      onControlChange?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`${action} failed: ${msg}`);
    } finally {
      setBusy(null);
      setConfirmAbort(false);
    }
  }

  const sortedEntries = tree.ordered.map((name) => ({
    name,
    depth: tree.depth[name] ?? 0,
    info: cacheEndpoints[name]!,
  }));

  return (
    <div
      className="glass rounded-2xl p-4 space-y-3"
      style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
    >
      {/* Header bar — status pill + totals + controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest"
            style={{ background: sessStyle.bg, color: sessStyle.text, border: `1px solid ${sessStyle.ring}` }}
          >
            {status}
          </span>
          {warningCount !== undefined && warningCount > 0 && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide"
              style={{ background: 'rgba(251,146,60,0.18)', color: 'rgb(253,186,116)', border: '1px solid rgba(251,146,60,0.45)' }}
              title={`${warningCount} non-fatal issues encountered`}
            >
              {warningCount} warning{warningCount === 1 ? '' : 's'}
            </span>
          )}
          <div className="text-xs text-text-dimmed font-mono whitespace-nowrap">
            {totals.ok}✓ · {totals.running}⟳ · {totals.fail}✗
            {' · '}
            <span className="text-text-primary">{totals.records.toLocaleString()}</span> records
            {totals.requests > 0 && (<>{' · '}{totals.requests} requests · {formatBytes(totals.bytes)}</>)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/sessions/$id/cache"
            params={{ id: sessionId }}
            className="rounded-lg px-3 py-1.5 text-xs font-medium"
            style={{ background: 'rgba(56,189,248,0.18)', color: 'rgb(125,211,252)', border: '1px solid rgba(56,189,248,0.45)' }}
          >
            View Records →
          </Link>
          {isRunning && (
            <>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => handleAction('pause')}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                style={{ background: 'rgba(56,189,248,0.18)', color: 'rgb(125,211,252)', border: '1px solid rgba(56,189,248,0.55)' }}
              >
                {busy === 'pause' ? 'Pausing…' : '❚❚ Pause'}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => setConfirmAbort(true)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                style={{ background: 'rgba(244,63,94,0.18)', color: 'rgb(253,164,175)', border: '1px solid rgba(244,63,94,0.55)' }}
              >
                ■ Abort
              </button>
            </>
          )}
          {isPaused && (
            <>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => handleAction('resume')}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                style={{ background: 'rgba(13,207,184,0.18)', color: 'rgb(94,234,212)', border: '1px solid rgba(13,207,184,0.55)' }}
              >
                {busy === 'resume' ? 'Resuming…' : '▶ Resume'}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => setConfirmAbort(true)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                style={{ background: 'rgba(244,63,94,0.18)', color: 'rgb(253,164,175)', border: '1px solid rgba(244,63,94,0.55)' }}
              >
                ■ Abort
              </button>
            </>
          )}
        </div>
      </div>

      {/* Abort confirmation dialog */}
      {confirmAbort && (
        <div
          className="rounded-xl p-3 text-sm flex items-center justify-between gap-3"
          style={{ background: 'rgba(244,63,94,0.12)', border: '1px solid rgba(244,63,94,0.45)' }}
        >
          <div className="text-rose-100">
            Stop all queued fetches? Records already cached will stay. This cannot be undone.
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setConfirmAbort(false)}
              className="rounded-lg px-3 py-1 text-xs font-medium"
              style={{ background: 'rgba(148,163,184,0.18)', color: 'rgb(203,213,225)', border: '1px solid rgba(148,163,184,0.45)' }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => handleAction('abort')}
              className="rounded-lg px-3 py-1 text-xs font-semibold disabled:opacity-50"
              style={{ background: 'rgb(244,63,94)', color: 'white' }}
            >
              {busy === 'abort' ? 'Aborting…' : 'Yes, abort'}
            </button>
          </div>
        </div>
      )}

      {sortedEntries.length === 0 && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          style={{ background: 'rgba(148,163,184,0.06)', border: '1px solid rgba(148,163,184,0.18)', color: 'var(--text-dimmed)' }}
        >
          {isTerminal
            ? 'No endpoint activity recorded for this session.'
            : 'Import starting — waiting for first cache job to report progress…'}
        </div>
      )}

      <div className="space-y-1.5">
        {sortedEntries.map(({ name, depth, info }) => (
          <EndpointCard
            key={name}
            name={name}
            info={info}
            depth={depth}
            expanded={expanded.has(name)}
            onToggle={() => setExpanded((prev) => {
              const next = new Set(prev);
              if (next.has(name)) next.delete(name);
              else next.add(name);
              return next;
            })}
          />
        ))}
      </div>

      {sessionCompleted && (
        <div
          className="rounded-xl px-4 py-3 text-sm flex items-center justify-between gap-3"
          style={{ background: 'rgba(13,207,184,0.10)', border: '1px solid rgba(13,207,184,0.35)' }}
        >
          <div className="text-emerald-100">
            <span className="font-semibold">Session finished.</span>{' '}
            <span className="font-mono text-xs">
              {sessionCompleted.canonicalRecordCount} canonical · {sessionCompleted.invalidRecordCount} invalid · {formatDuration(sessionCompleted.durationMs)}
            </span>
          </div>
          <Link
            to="/sessions/$id/cache"
            params={{ id: sessionId }}
            className="rounded-lg px-3 py-1 text-xs font-medium"
            style={{ background: 'rgba(13,207,184,0.18)', color: 'rgb(94,234,212)', border: '1px solid rgba(13,207,184,0.55)' }}
          >
            Review records →
          </Link>
        </div>
      )}
    </div>
  );
}
